import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import api from "../../utils/api";
import {
  _findSelectOption,
  _capitalized,
  _isFirstRender,
  _getSkipByPage,
  _getBaseUrl,
  _getCSRFTokenValue,
  _getTranslation,
} from "../../utils/helper";

import { Loader, Pagination } from "../../atoms";
import {
  Datepicker,
  InputSearch,
  InputSelect,
  MobileFilters,
} from "../../forms";
import { CardReferenceDoc } from "../../cards";

const ReferenceDocOverview = (props) => {
  const INITIAL_VALUES = {
    searchTerm: "",
    type: "",
    startdate: "",
    enddate: "",
    page: 1,
  };
  const filterListOrder = ["type"];
  const [filterOptions, setFilterOptions] = useState([]);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_VALUES);
  const [posts, setPosts] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [areFiltersActive, setAreFiltersActive] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const isFirstLoad = _isFirstRender();

  useEffect(() => {
    fetchFilters();
    fetchPosts();
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      // sync the values when filters are applied
      setValues(appliedFilters);

      fetchPosts();
    }
  }, [appliedFilters]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);
    api
      .get("/reference_doc_filter_options")
      .then((res) => {
        setIsLoadingFilters(false);
        const filtersData = res.data;
        const formattedList = [];
        Object.keys(filtersData).map((key) => {
          formattedList.push({
            label: _capitalized(key),
            key: key,
            options: filtersData[key],
          });
        });
        setFilterOptions(formattedList);
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);
    const POSTS_PER_PAGE = 12;
    const POSTS_SKIP = _getSkipByPage(appliedFilters.page, POSTS_PER_PAGE);
    const fields = {
      ...appliedFilters,
      skip: POSTS_SKIP,
      take: POSTS_PER_PAGE,
    };

    // remove irrelevant keys
    delete fields.page;

    const csrfToken = await _getCSRFTokenValue();
    api
      .post("/reference_doc", fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        const TOTAL_POSTS = res.data.totalNodes;
        const TOTAL_PAGES = Math.ceil(TOTAL_POSTS / POSTS_PER_PAGE);
        setIsLoadingPosts(false);
        setPosts(res.data.contentData);
        setTotalPages(TOTAL_PAGES);
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const getFilterInputs = () => {
    return isLoadingFilters ? (
      <Loader />
    ) : (
      <React.Fragment>
        <div className="filter-item">
          <label className="hidden sm:block font-semibold mb-2">
            {_getTranslation("filter_by")}
          </label>
          {filterListOrder.map((key) => {
            const foundFilterOptions = filterOptions.find(
              (opt) => opt.key === key
            );

            return (
              foundFilterOptions && (
                <InputSelect
                  name={foundFilterOptions.key}
                  placeholder={`All ${foundFilterOptions.key}s`}
                  options={foundFilterOptions.options}
                  isClearable
                  isSearchable
                  value={_findSelectOption(
                    foundFilterOptions.options,
                    values[foundFilterOptions.key]
                  )}
                  onChange={(name, selectedValues) => {
                    setValues({
                      ...values,
                      [foundFilterOptions.key]: selectedValues,
                    });
                  }}
                />
              )
            );
          })}
        </div>

        <div className="flex items-start gap-4">
          <label className="mt-4 text-sm w-10">{_getTranslation("from")}</label>
          <Datepicker
            value={values.startdate}
            class="flex-1"
            onChange={(selected) => {
              setValues({
                ...values,
                startdate: selected,
                enddate: null,
              });
            }}
          />
        </div>
        <div className="flex items-start gap-4">
          <label className="mt-4 text-sm w-10">{_getTranslation("to")}</label>
          <Datepicker
            value={values.enddate}
            class={`flex-1 ${
              _.isEmpty(values.startdate)
                ? "pointer-events-none opacity-50"
                : ""
            }`}
            onChange={(selected) => {
              setValues({
                ...values,
                enddate: selected,
              });
            }}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-y-2 gap-x-4 lg:gap-x-2 mt-4">
          <button
            className="btn btn-s--pill sm:flex-1"
            onClick={() => applyFilters()}
          >
            {_getTranslation("apply_filters")}
          </button>
          {!_.isEqual(values, INITIAL_VALUES) && (
            <button
              className="btn btn-s--pill btn-c--hollow---black sm:flex-1"
              onClick={() => resetFilters()}
            >
              {_getTranslation("reset_filters")}
            </button>
          )}
        </div>
      </React.Fragment>
    );
  };

  const removeTag = (tag) => {
    const filterKey = tag?.filterKey;

    if (filterKey) {
      const currentValues = values[filterKey];
      let updatedValues = { ...values };

      if (_.isArray(currentValues)) {
        const newValues = currentValues.filter((item) => item !== tag.value);
        updatedValues[filterKey] = newValues;
      } else if (_.isString(currentValues) || _.isObject(currentValues)) {
        let newValue = "";

        // uncomment this if newValue's default value is not empty string
        // if (filterKey === "search_term") {
        //   newValue = "";
        // }

        updatedValues[filterKey] = newValue;
      }

      // setValues(updatedValues);
      setAppliedFilters(updatedValues);
    }
  };

  const FilterTags = useCallback(() => {
    const selectedTags = [];

    Object.keys(appliedFilters).map((filterKey) => {
      const filterCategory = appliedFilters[filterKey];
      if (!_.isEmpty(filterCategory)) {
        if (_.isArray(filterCategory)) {
          filterCategory.map((item) => {
            let foundOption = false;
            filterOptions.find((filterCatItem) => {
              if (filterCatItem.key === filterKey) {
                foundOption = filterCatItem.options.find(
                  (opt) => opt.value === item
                );
              }
            });

            selectedTags.push({
              label: foundOption?.label || item,
              value: item,
              filterKey,
            });
          });
        } else if (_.isString(filterCategory)) {
          selectedTags.push({
            label: filterCategory,
            filterKey,
          });
        } else if (_.isObject(filterCategory)) {
          selectedTags.push({
            label: filterCategory.label,
            value: filterCategory.value,
            filterKey,
          });
        }
      }
    });

    return !_.isEmpty(selectedTags) ? (
      <div className="filter-tags-wrapper flex flex-wrap mb-8">
        {selectedTags.map((tag) => (
          <button
            key={tag}
            className="filter-tag"
            onClick={() => {
              removeTag(tag);
            }}
          >
            {tag.label}
          </button>
        ))}
        <button
          className="filter-tag filter-tag-reset"
          onClick={() => resetFilters()}
        >
          {_getTranslation("clear_all")}
        </button>
      </div>
    ) : null;
  }, [appliedFilters]);

  const applyFilters = (pageNo = 1, isFiltersActive = true) => {
    setAppliedFilters({ ...values, page: pageNo });

    if (isFiltersActive) {
      setAreFiltersActive(true);
    }
  };

  const resetFilters = () => {
    setValues(INITIAL_VALUES);
    setAppliedFilters(INITIAL_VALUES);
    setAreFiltersActive(false);
  };

  return (
    <div className="container mb-8 md:mb-12 xl:mb-16 grid lg:flex gap-8 md:gap-x-12">
      <div className="hidden lg:block w-[330px]">
        <InputSearch
          class="w-full mb-4"
          placeholder={_getTranslation("type_in_your_keywords")}
          value={values.searchTerm}
          onChange={(e) => {
            const value = e.target.value;
            setValues({ ...values, searchTerm: value });
          }}
        />

        <div className="grid gap-2">{getFilterInputs()}</div>
      </div>

      <MobileFilters class="w-full lg:hidden">
        <div className="filter-item">
          <InputSearch
            class="w-full"
            placeholder={_getTranslation("type_in_your_keywords")}
            value={values.searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setValues({ ...values, searchTerm: value });
            }}
          />
        </div>
        {getFilterInputs()}
      </MobileFilters>

      <div className="flex-1">
        <FilterTags />

        {isLoadingPosts && <Loader class="mb-8" />}
        {_.isArray(posts) && posts.length > 0 ? (
          <React.Fragment>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-x-8 lg:gap-4 xl:gap-x-8 mb-8">
              {posts.map((post) => (
                <CardReferenceDoc data={post} />
              ))}
            </div>
            <div className="flex items-center justify-center gap-x-6 mt-8">
              <Pagination
                page={appliedFilters.page}
                totalPages={totalPages}
                setPage={(newPage) => {
                  if (areFiltersActive) {
                    applyFilters(newPage);
                  } else {
                    applyFilters(newPage, false);
                  }
                }}
              />
              {isLoadingPosts && <Loader />}
            </div>
          </React.Fragment>
        ) : isLoadingPosts ? null : (
          <p>{_getTranslation("there_are_no_posts_available")}.</p>
        )}
      </div>
    </div>
  );
};

export default ReferenceDocOverview;
